import axiosInstance from '../../Api/axiosInstance';

const API_URL = '/crm/outreach';

const call = async (request, fallback) => {
	try {
		const response = await request();
		return response.data;
	} catch (error) {
		throw new Error(
			error.response?.data?.msg || error.response?.data?.error || fallback,
		);
	}
};

/** Whether the platform has the Outreach tracker switched on for the caller's company. */
export const getOutreachAccess = () =>
	call(
		() => axiosInstance.get('/crm/outreach-access'),
		'Error checking Outreach tracker access.',
	);

// The backend always scopes leads to the signed-in user's company; companyId is only passed
// through for consistency with the rest of the app.
export const getLeads = (companyId) =>
	call(
		() => axiosInstance.get(API_URL, { params: { companyId } }),
		'Error fetching leads.',
	);

export const getLeadById = (leadId, companyId) =>
	call(
		() => axiosInstance.get(`${API_URL}/${leadId}`, { params: { companyId } }),
		'Error fetching lead.',
	);

export const createLead = (data, companyId) =>
	call(
		() => axiosInstance.post(API_URL, data, { params: { companyId } }),
		'Error creating lead.',
	);

export const importLeadsCsv = (csv, companyId) =>
	call(
		() =>
			axiosInstance.post(`${API_URL}/import`, { csv }, { params: { companyId } }),
		'Error importing the CSV.',
	);

export const updateLead = (leadId, data, companyId) =>
	call(
		() =>
			axiosInstance.patch(`${API_URL}/${leadId}`, data, { params: { companyId } }),
		'Error updating lead.',
	);

export const deleteLead = (leadId, companyId) =>
	call(
		() => axiosInstance.delete(`${API_URL}/${leadId}`, { params: { companyId } }),
		'Error deleting lead.',
	);

export const logLeadEvent = (leadId, data, companyId) =>
	call(
		() =>
			axiosInstance.post(`${API_URL}/${leadId}/log`, data, {
				params: { companyId },
			}),
		'Error saving to the timeline.',
	);

/** Sends the lead's next email from the caller's Gmail and logs it. `kind`: emailed | follow_up_1 | follow_up_2. */
export const sendLeadEmail = (leadId, kind, companyId) =>
	call(
		() =>
			axiosInstance.post(
				`${API_URL}/${leadId}/send`,
				{ kind },
				{ params: { companyId } },
			),
		'Error sending the email.',
	);

export const deleteLeadEvent = (leadId, eventId, companyId) =>
	call(
		() =>
			axiosInstance.delete(`${API_URL}/${leadId}/events/${eventId}`, {
				params: { companyId },
			}),
		'Error deleting the timeline entry.',
	);

export const addLeadContact = (leadId, data, companyId) =>
	call(
		() =>
			axiosInstance.post(`${API_URL}/${leadId}/contacts`, data, {
				params: { companyId },
			}),
		'Error adding the contact.',
	);

export const updateLeadContact = (leadId, contactId, data, companyId) =>
	call(
		() =>
			axiosInstance.patch(`${API_URL}/${leadId}/contacts/${contactId}`, data, {
				params: { companyId },
			}),
		'Error updating the contact.',
	);

export const deleteLeadContact = (leadId, contactId, companyId) =>
	call(
		() =>
			axiosInstance.delete(`${API_URL}/${leadId}/contacts/${contactId}`, {
				params: { companyId },
			}),
		'Error deleting the contact.',
	);

/** Omit `contactIds` to check every contact with an email. Uses verification credits. */
export const verifyLeadContactEmails = (
	leadId,
	{ contactIds, provider },
	companyId,
) =>
	call(
		() =>
			axiosInstance.post(
				`${API_URL}/${leadId}/verify-emails`,
				{
					...(contactIds ? { contactIds } : {}),
					...(provider ? { provider } : {}),
				},
				{ params: { companyId } },
			),
		'Error verifying the email addresses.',
	);
